import {
	type AssetType,
	type Form,
	type IssueType,
	type ProcedureInitiative,
	type ProcedureType,
} from "@overmap-ai/common"
import {
	deleteAssetType,
	deleteForm,
	deleteIssueType,
	deleteProcedureInitiative,
	deleteProcedureType,
	persistSliceReducer,
	RESET_ORGANIZATION_STATE,
	RESET_PROJECT_STATE,
	RESET_STATE,
} from "@overmap-ai/core"
import { createSlice, isAnyOf, type PayloadAction } from "@reduxjs/toolkit"
import type { SortingState, VisibilityState } from "@tanstack/react-table"

import type { AnySerializedFilter } from "../../filtering/filter"
import { APP_DATA_DB_NAME } from "../constants"

export interface FilterSliceState {
	assetTypeFilters: Record<AssetType["uuid"], AnySerializedFilter[]>
	assetTypeColumnVisibility: Record<AssetType["uuid"], VisibilityState>
	assetTypeColumnSorting: Record<AssetType["uuid"], SortingState>
	issueTypeFilters: Record<IssueType["uuid"], AnySerializedFilter[]>
	issueTypeColumnVisibility: Record<IssueType["uuid"], VisibilityState>
	issueTypeColumnSorting: Record<IssueType["uuid"], SortingState>
	formSubmissionFilters: Record<Form["uuid"], AnySerializedFilter[]>
	formSubmissionColumnVisibility: Record<Form["uuid"], VisibilityState>
	formSubmissionColumnSorting: Record<Form["uuid"], SortingState>
	procedureInitiativeDataFilters: Record<ProcedureInitiative["uuid"], AnySerializedFilter[]>
	procedureInitiativeDataColumnVisibility: Record<ProcedureInitiative["uuid"], VisibilityState>
	procedureInitiativeDataColumnSorting: Record<ProcedureInitiative["uuid"], SortingState>
	procedureTypeDataFilters: Record<ProcedureType["uuid"], AnySerializedFilter[]>
	procedureTypeDataColumnVisibility: Record<ProcedureType["uuid"], VisibilityState>
	procedureTypeDataColumnSorting: Record<ProcedureType["uuid"], SortingState>
	formFilters: AnySerializedFilter[]
	projectFilters: AnySerializedFilter[]
	procedureInitiativeFilters: AnySerializedFilter[]
}

const initialState: FilterSliceState = {
	assetTypeFilters: {},
	assetTypeColumnVisibility: {},
	assetTypeColumnSorting: {},
	issueTypeFilters: {},
	issueTypeColumnVisibility: {},
	issueTypeColumnSorting: {},
	formSubmissionFilters: {},
	formSubmissionColumnVisibility: {},
	formSubmissionColumnSorting: {},
	procedureInitiativeDataFilters: {},
	procedureInitiativeDataColumnVisibility: {},
	procedureInitiativeDataColumnSorting: {},
	procedureTypeDataFilters: {},
	procedureTypeDataColumnVisibility: {},
	procedureTypeDataColumnSorting: {},
	formFilters: [],
	projectFilters: [],
	procedureInitiativeFilters: [],
}

interface SetFiltersPayload {
	id: string
	filters: AnySerializedFilter[]
}

interface SetColumnVisibilityPayload<TId extends PropertyKey> {
	id: TId
	columnVisibility: VisibilityState
}

interface SetColumnSortingPayload<TId extends PropertyKey> {
	id: TId
	columnSorting: SortingState
}

export const filterSlice = createSlice({
	name: "filters",
	initialState,
	extraReducers: (builder) =>
		builder
			.addCase(deleteAssetType, (state, action) => {
				delete state.assetTypeFilters[action.payload]
				delete state.assetTypeColumnSorting[action.payload]
				delete state.assetTypeColumnVisibility[action.payload]
			})
			.addCase(deleteIssueType, (state, action) => {
				delete state.issueTypeFilters[action.payload]
				delete state.issueTypeColumnSorting[action.payload]
				delete state.issueTypeColumnVisibility[action.payload]
			})
			.addCase(deleteForm, (state, action) => {
				delete state.formSubmissionFilters[action.payload]
				delete state.formSubmissionColumnSorting[action.payload]
				delete state.formSubmissionColumnVisibility[action.payload]
			})
			.addCase(deleteProcedureInitiative, (state, action) => {
				delete state.procedureInitiativeDataFilters[action.payload]
				delete state.procedureInitiativeDataColumnSorting[action.payload]
				delete state.procedureInitiativeDataColumnVisibility[action.payload]
			})
			.addCase(deleteProcedureType, (state, action) => {
				delete state.procedureTypeDataFilters[action.payload]
				delete state.procedureTypeDataColumnSorting[action.payload]
				delete state.procedureTypeDataColumnVisibility[action.payload]
			})
			.addMatcher(isAnyOf(RESET_STATE, RESET_ORGANIZATION_STATE, RESET_PROJECT_STATE), (state) => {
				Object.assign(state, initialState)
			}),
	reducers: {
		setAssetTypeFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.assetTypeFilters[id] = filters
		},
		setAssetTypeColumnVisibility: (state, action: PayloadAction<SetColumnVisibilityPayload<AssetType["uuid"]>>) => {
			const { id, columnVisibility } = action.payload
			state.assetTypeColumnVisibility[id] = columnVisibility
		},
		setAssetTypeColumnSorting: (state, action: PayloadAction<SetColumnSortingPayload<AssetType["uuid"]>>) => {
			const { id, columnSorting } = action.payload
			state.assetTypeColumnSorting[id] = columnSorting
		},
		setFormSubmissionFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.formSubmissionFilters[id] = filters
		},
		setFormSubmissionColumnVisibility: (state, action: PayloadAction<SetColumnVisibilityPayload<Form["uuid"]>>) => {
			const { id, columnVisibility } = action.payload
			state.formSubmissionColumnVisibility[id] = columnVisibility
		},
		setFormSubmissionColumnSorting: (state, action: PayloadAction<SetColumnSortingPayload<Form["uuid"]>>) => {
			const { id, columnSorting } = action.payload
			state.formSubmissionColumnSorting[id] = columnSorting
		},
		setIssueTypeFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.issueTypeFilters[id] = filters
		},
		setIssueTypeColumnVisibility: (state, action: PayloadAction<SetColumnVisibilityPayload<IssueType["uuid"]>>) => {
			const { id, columnVisibility } = action.payload
			state.issueTypeColumnVisibility[id] = columnVisibility
		},
		setIssueTypeColumnSorting: (state, action: PayloadAction<SetColumnSortingPayload<IssueType["uuid"]>>) => {
			const { id, columnSorting } = action.payload
			state.issueTypeColumnSorting[id] = columnSorting
		},
		setProcedureInitiativeDataFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.procedureInitiativeDataFilters[id] = filters
		},
		setProcedureInitiativeDataColumnVisibility: (
			state,
			action: PayloadAction<SetColumnVisibilityPayload<ProcedureInitiative["uuid"]>>,
		) => {
			const { id, columnVisibility } = action.payload
			state.procedureInitiativeDataColumnVisibility[id] = columnVisibility
		},
		setProcedureInitiativeDataColumnSorting: (
			state,
			action: PayloadAction<SetColumnSortingPayload<ProcedureInitiative["uuid"]>>,
		) => {
			const { id, columnSorting } = action.payload
			state.procedureInitiativeDataColumnSorting[id] = columnSorting
		},
		setProcedureTypeDataFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.procedureTypeDataFilters[id] = filters
		},
		setProcedureTypeDataColumnVisibility: (
			state,
			action: PayloadAction<SetColumnVisibilityPayload<ProcedureType["uuid"]>>,
		) => {
			const { id, columnVisibility } = action.payload
			state.procedureTypeDataColumnVisibility[id] = columnVisibility
		},
		setProcedureTypeDataColumnSorting: (
			state,
			action: PayloadAction<SetColumnSortingPayload<ProcedureType["uuid"]>>,
		) => {
			const { id, columnSorting } = action.payload
			state.procedureTypeDataColumnSorting[id] = columnSorting
		},
		setFormFilters: (state, action: PayloadAction<AnySerializedFilter[]>) => {
			state.formFilters = action.payload
		},
		setProjectFilters: (state, action: PayloadAction<AnySerializedFilter[]>) => {
			state.projectFilters = action.payload
		},
		setProcedureInitiativeFilters: (state, action: PayloadAction<AnySerializedFilter[]>) => {
			state.procedureInitiativeFilters = action.payload
		},
	},
})

export const {
	setAssetTypeFilters,
	setAssetTypeColumnVisibility,
	setAssetTypeColumnSorting,
	setFormSubmissionFilters,
	setFormSubmissionColumnVisibility,
	setFormSubmissionColumnSorting,
	setFormFilters,
	setIssueTypeFilters,
	setIssueTypeColumnVisibility,
	setIssueTypeColumnSorting,
	setProjectFilters,
	setProcedureInitiativeDataFilters,
	setProcedureInitiativeDataColumnVisibility,
	setProcedureInitiativeDataColumnSorting,
	setProcedureTypeDataFilters,
	setProcedureTypeDataColumnVisibility,
	setProcedureTypeDataColumnSorting,
	setProcedureInitiativeFilters,
} = filterSlice.actions

export const filterSliceReducer = persistSliceReducer(filterSlice, APP_DATA_DB_NAME)
